import { useEffect, useRef } from 'react';

/**
 * Listens for cursor moves over elements marked with
 * `data-live-focus-key="..."` and broadcasts a `focus_target` event with
 * the key whenever the focus changes (entering, leaving, or moving from
 * one tagged element to another).
 *
 * Pairs with RemoteHoverFocus on the receiver side, which paints a
 * colored outline around the same element. The point is to convey
 * "I'm pointing at this thing" semantically rather than via raw pixel
 * coordinates — robust to differing viewport sizes between teacher and
 * student.
 */
export function useFocusBroadcast(perform) {
  const lastKeyRef = useRef(null);

  useEffect(() => {
    if (!perform) return;

    const handleOver = (e) => {
      const target = e.target?.closest?.('[data-live-focus-key]');
      const key = target ? target.getAttribute('data-live-focus-key') : null;
      if (key === lastKeyRef.current) return;
      lastKeyRef.current = key;
      perform('focus_target', { key });
    };

    document.addEventListener('mouseover', handleOver);
    // When the cursor leaves the document entirely, clear the focus.
    const handleLeave = () => {
      if (lastKeyRef.current === null) return;
      lastKeyRef.current = null;
      perform('focus_target', { key: null });
    };
    document.addEventListener('mouseleave', handleLeave);

    return () => {
      document.removeEventListener('mouseover', handleOver);
      document.removeEventListener('mouseleave', handleLeave);
    };
  }, [perform]);
}
